// Type-safe API-Client für die CA-App.
// Pfade respektieren das `/ca`-Prefix der Caddy-Route + FastAPI-`root_path`.

import type { DepthLabel } from "@/lib/depth";

const API_BASE = "/ca/api/v1";

export class APIError extends Error {
  status: number;
  detail: string;
  data?: unknown;
  constructor(status: number, detail: string, data?: unknown) {
    super(`API ${status}: ${detail}`);
    this.status = status;
    this.detail = detail;
    this.data = data;
  }
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem("ca_access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Silent Refresh: Bei 401 einmalig den Refresh-Token einlösen und den
// Original-Request wiederholen, statt den rohen "Signature has expired"-Fehler
// in die UI durchzureichen (Fund aus Franz' Live-Test 2026-08-02). Single-flight,
// damit parallele 401s nur einen Refresh auslösen.
let refreshPromise: Promise<boolean> | null = null;

function tryRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem("ca_refresh_token");
  if (!refreshToken) return Promise.resolve(false);
  refreshPromise ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const tokens = await res.json();
      localStorage.setItem("ca_access_token", tokens.access_token);
      localStorage.setItem("ca_refresh_token", tokens.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

function forceRelogin(): void {
  localStorage.removeItem("ca_access_token");
  localStorage.removeItem("ca_refresh_token");
  window.location.assign(`${import.meta.env.BASE_URL}login`);
}

// Credit-Balance hat sich geändert (bezahlte Aktion oder 402) — der Header
// lädt daraufhin /auth/me neu und zeigt den frischen Stand.
export const CREDITS_CHANGED_EVENT = "ca:credits-changed";

// POST-Pfade, die Credits kosten (Backend: app/services/credits.py).
const PAID_POST_MARKERS = [
  "/audit",
  "propose-fix",
  "/wizard/suggest",
  "/assist/rewrite",
  "/assist/apply-strategy",
  "/assist/translate",
  "/chat/message",
  "/extract",
  "/license/activate",
];

export function notifyCreditsChanged(): void {
  window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT));
}

// Silent Refresh gilt auch für /auth/me — der App-Boot ruft es parallel zu
// den Daten-Requests, und loadMe() räumt bei einem unbehandelten 401 die
// Tokens weg (Fund aus J11 der ux-abnahme 2026-08-02: erfolgreiches Refresh
// eines parallel laufenden Requests, trotzdem Redirect nach /login).
// Ausgenommen bleiben nur Login (401 = falsches Passwort, kein Refresh) und
// der Refresh-Call selbst (darf nicht rekursiv refreshen).
const NO_REFRESH_PATHS = ["/auth/login", "/auth/refresh"];

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
      ...(init.headers || {}),
    },
  });
  if (
    response.status === 401 &&
    !NO_REFRESH_PATHS.some((p) => path.startsWith(p)) &&
    !retried
  ) {
    if (await tryRefresh()) {
      return request<T>(path, init, true);
    }
    forceRelogin();
  }
  if (!response.ok) {
    if (response.status === 402) notifyCreditsChanged();
    let detail = response.statusText;
    let data: unknown;
    try {
      const body = await response.json();
      data = body.detail;
      detail =
        typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail ?? body);
    } catch {
      // ignore
    }
    throw new APIError(response.status, detail, data);
  }
  if (response.status === 204) return null as T;
  const result = (await response.json()) as T;
  if (
    (init.method ?? "GET").toUpperCase() === "POST" &&
    PAID_POST_MARKERS.some((m) => path.includes(m))
  ) {
    notifyCreditsChanged();
  }
  return result;
}

// --- Types ---

// CourseFormat ist seit V0.3 offen: 88 Formate aus `app/data/course_formats.json`
// werden über /wizard/catalog geliefert. Der String wird gespeichert wie er
// vom Catalog kommt; alte Werte ("lecture", "blended", "async_online") werden
// auf Anzeige-Ebene über `LEGACY_FORMAT_ALIASES` resolved.
export type CourseFormat = string;
export type CourseModality = "presence" | "synchronous" | "asynchronous" | "blended";
export type BenderDepth = "Wissen" | "Verstehen" | "Können" | "Performanz";

export interface ILO {
  id: string;
  course_id: string;
  position: number;
  text: string;
  action_verb?: string;
  bender_depth?: BenderDepth;
  bloom_level?: number;
  bloom_domain?: string;
  content_area?: string;
  measurability_score?: number;
}

// Multi-Session-Formate (F4): zeitliche Phase einer Aktivität
// pre = Vorbereitung, in = in der Session, between = Transfer, post = Nachbereitung
export type TLAPhase = "pre" | "in" | "between" | "post";

export type SessionKind =
  | "presence_day"
  | "online"
  | "coaching"
  | "group_meeting"
  | "wol_cycle"
  | "exam"
  | "custom";

export interface TlaAttachment {
  title: string;
  content: string;
  created_at: string;
}

export interface TLA {
  id: string;
  course_id: string;
  position: number;
  type?: string;
  modality?: string;
  duration_minutes?: number;
  description?: string;
  scaffolding_position?: string;
  phase?: TLAPhase;
  session_id?: string | null;
  bloom_levels?: number[];
  linked_ilo_ids: string[];
  /** Freies JSON-Feld; KAI-Anhänge liegen unter extras.attachments. */
  extras?: { attachments?: TlaAttachment[] } & Record<string, unknown>;
}

export interface AT {
  id: string;
  course_id: string;
  position: number;
  type?: string;
  format?: string;
  weight?: number;
  description?: string;
  duration_minutes?: number;
  session_id?: string | null;
  bloom_levels?: number[];
  linked_ilo_ids: string[];
}

export interface CourseSession {
  id: string;
  course_id: string;
  position: number;
  title: string;
  kind: SessionKind;
  date?: string | null;
  start?: string | null;
  end?: string | null;
  modality?: string | null;
  notes?: string | null;
}

export type SessionPayload = Partial<
  Omit<CourseSession, "id" | "course_id" | "position">
>;

export interface SessionSeriesPayload {
  title: string;
  kind: SessionKind;
  count: number;
  interval_weeks: number;
  start_date?: string | null;
  day_start?: string | null;
  day_end?: string | null;
  modality?: string | null;
}

export interface Course {
  id: string;
  user_id: string;
  title: string;
  audience?: string;
  format?: CourseFormat;
  modality?: CourseModality;
  duration_hours?: number;
  ects_credits?: number;
  status: "draft" | "in_review" | "approved" | "archived";
  created_at: string;
  updated_at: string;
  ilos?: ILO[];
  tlas?: TLA[];
  ats?: AT[];
  // Freie Metadaten: context_kind, rough_idea (Seminarbeschreibung), trainer_guide
  metadata?: Record<string, unknown>;
}

export interface PerILOAnalysis {
  ilo_id: string;
  bender_depth_observed?: BenderDepth;
  is_aligned: boolean;
  critique: string;
  improvement: string;
}

export interface DetectedFlaw {
  flaw_type?: string;
  severity?: "critical" | "recommended" | "optional";
  description?: string;
  recommendation?: string;
  // Ziel-Entität des Befunds (Backend: app/schemas/audit.py DetectedFlaw) —
  // der Workspace nutzt sie, um Marker direkt an der betroffenen Karte zu
  // zeigen (Designkonzept 2026-08-13 §2).
  target_ilo_id?: string | null;
  target_tla_id?: string | null;
  target_at_id?: string | null;
}

// Vollständigkeits-Gate (docs/audit-vollstaendigkeits-gate.md)
export interface FeasibilityFinding {
  code: string;
  message: string;
  ref: number[] | null;
  fix_step: number;
}

export interface FeasibilityReport {
  feasible: boolean;
  blockers: FeasibilityFinding[];
  warnings: FeasibilityFinding[];
}

// Trainingsleitfaden (docs/feature-spec-export-leitfaden.md)
export interface TrainerGuideBreak {
  start: string;
  end: string;
}

// Automatische Pausenregel: nach jeweils `every_minutes` Netto-Verplanungszeit
// eine Pause von `break_minutes` (Backend erzeugt daraus Auto-Pausen).
export interface TrainerGuideBreakRule {
  every_minutes: number;
  break_minutes: number;
}

export interface GuideItemPayload {
  title: string;
  description?: string | null;
  duration_minutes?: number | null;
  kind?: "method" | "custom";
  linked_ilos?: number[];
  attachments?: TlaAttachment[];
}

export interface TrainerGuideRow {
  start: string;
  end: string;
  kind: "method" | "break" | "buffer" | "custom";
  title: string;
  description?: string | null;
  duration_minutes: number;
  linked_ilos: number[];
  attachments?: TlaAttachment[];
  // true = automatisch erzeugt (Auto-Pause, prozentualer Puffer)
  auto?: boolean;
}

export interface TrainerGuide {
  date: string;
  rows: TrainerGuideRow[];
  method_minutes: number;
  available_minutes: number;
  overflow_minutes: number;
  utilization_pct: number;
  missing_durations: number;
  buffer_minutes: number;   // Summe der prozentualen Puffer
  auto_breaks: number;      // Anzahl der Auto-Pausen
  realistic_load_pct: number; // (Methoden + Puffer) vs. verfügbar
}

// 422-Body des Audit-Gates (error = "audit_not_feasible")
export interface AuditNotFeasibleDetail {
  error: "audit_not_feasible";
  message: string;
  blockers: FeasibilityFinding[];
  warnings: FeasibilityFinding[];
}

export function isFeasibilityDetail(data: unknown): data is AuditNotFeasibleDetail {
  return (
    !!data &&
    typeof data === "object" &&
    (data as { error?: string }).error === "audit_not_feasible"
  );
}

// 402-Payload des Credit-Systems (Backend: app/services/credits.py) —
// das Frontend rendert daraus die „Guthaben aufgebraucht"-Karte.
export interface QuotaDetail {
  error: "credits_exhausted";
  message: string;
  feature: string;
  cost: number;
  balance: number;
  shop_url: string;
}

export function isQuotaDetail(data: unknown): data is QuotaDetail {
  return (
    !!data &&
    typeof data === "object" &&
    (data as { error?: string }).error === "credits_exhausted"
  );
}

export function isQuotaError(err: unknown): err is APIError {
  return err instanceof APIError && err.status === 402;
}

// Öffentliche Config (GET /config/public, ohne Auth — auch für /activate).
export interface PublicConfig {
  shop_url: string;
  credit_costs: Record<string, number>;
}

// Lizenz-Aktivierung (POST /license/activate, Backend: app/routes/license.py)
export interface ActivateResponse {
  ok: boolean;
  already_active: boolean;
  credits_granted: number;
  balance: number;
  status: string;
}

export interface AnalysisReport {
  id: string;
  course_id: string;
  performance_level?: number;  bender_label?: string;
  overall_score?: number;
  summary?: string;
  theoretical_insight?: string;
  alignment_matrix: Record<string, unknown>;
  detected_flaws: DetectedFlaw[];
  rag_feedback: Array<Record<string, unknown>>;
  per_ilo_analysis: PerILOAnalysis[];
  metric_results: Record<string, unknown>;
  model_used?: string;
  generated_at: string;
  duration_ms?: number;
}

export interface UserMe {
  id: string;
  email: string;
  firstname?: string;
  lastname?: string;
  full_name?: string;
  institution?: string;
  company?: string;
  position?: string;
  role: "user" | "admin";
  tier: "novice" | "practitioner" | "scientist";
  status: "pending" | "active" | "rejected" | "archived";
  audit_balance: number;
  audit_lifetime_used: number;
}

export interface RegisterPayload {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  company?: string;
  position?: string;
  privacy_accepted: boolean;
  marketing_consent: boolean;
}

// --- Endpoints ---

export const api = {
  // Auth
  register: (payload: RegisterPayload) =>
    request<UserMe>("/auth/register", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  login: (email: string, password: string) =>
    request<{ access_token: string; refresh_token: string; expires_in: number }>(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
    ),
  me: () => request<UserMe>("/auth/me"),
  logout: (refresh_token: string) =>
    request("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refresh_token }),
    }),

  // Lizenz + öffentliche Config (Credit-Pakete aus dem Odoo-Shop)
  activateLicense: (code: string) =>
    request<ActivateResponse>("/license/activate", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  publicConfig: () => request<PublicConfig>("/config/public"),

  // Courses
  listCourses: () => request<Course[]>("/courses"),
  createCourse: (body: Partial<Course>) =>
    request<Course>("/courses", { method: "POST", body: JSON.stringify(body) }),
  getCourse: (id: string) => request<Course>(`/courses/${id}`),
  updateCourse: (id: string, body: Partial<Course>) =>
    request<Course>(`/courses/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  trainerGuide: (courseId: string, body: {
    date: string;
    start: string;
    end: string;
    breaks: TrainerGuideBreak[];
    items?: GuideItemPayload[];
    buffer_pct?: number;
    break_rule?: TrainerGuideBreakRule | null;
    session_id?: string | null;
  }) =>
    request<TrainerGuide>(`/courses/${courseId}/trainer-guide`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  archiveCourse: (id: string) =>
    request(`/courses/${id}`, { method: "DELETE" }),

  // ILOs
  createILO: (courseId: string, body: Partial<ILO>) =>
    request<ILO>(`/courses/${courseId}/ilos`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateILO: (courseId: string, iloId: string, body: Partial<ILO>) =>
    request<ILO>(`/courses/${courseId}/ilos/${iloId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteILO: (courseId: string, iloId: string) =>
    request(`/courses/${courseId}/ilos/${iloId}`, { method: "DELETE" }),

  // TLAs
  createTLA: (courseId: string, body: Partial<TLA>) =>
    request<TLA>(`/courses/${courseId}/tlas`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateTLA: (courseId: string, tlaId: string, body: Partial<TLA>) =>
    request<TLA>(`/courses/${courseId}/tlas/${tlaId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteTLA: (courseId: string, tlaId: string) =>
    request(`/courses/${courseId}/tlas/${tlaId}`, { method: "DELETE" }),

  // ATs
  createAT: (courseId: string, body: Partial<AT>) =>
    request<AT>(`/courses/${courseId}/ats`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateAT: (courseId: string, atId: string, body: Partial<AT>) =>
    request<AT>(`/courses/${courseId}/ats/${atId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteAT: (courseId: string, atId: string) =>
    request(`/courses/${courseId}/ats/${atId}`, { method: "DELETE" }),

  // Sessions (Multi-Session-Formate)
  listSessions: (courseId: string) =>
    request<CourseSession[]>(`/courses/${courseId}/sessions`),
  createSession: (courseId: string, body: SessionPayload) =>
    request<CourseSession>(`/courses/${courseId}/sessions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateSession: (courseId: string, sessionId: string, body: SessionPayload) =>
    request<CourseSession>(`/courses/${courseId}/sessions/${sessionId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteSession: (courseId: string, sessionId: string) =>
    request(`/courses/${courseId}/sessions/${sessionId}`, { method: "DELETE" }),
  createSessionSeries: (courseId: string, body: SessionSeriesPayload) =>
    request<CourseSession[]>(`/courses/${courseId}/sessions/series`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Audit
  runAudit: (courseId: string) =>
    request<AnalysisReport>(`/courses/${courseId}/audit`, { method: "POST" }),
  auditFeasibility: (courseId: string) =>
    request<FeasibilityReport>(`/courses/${courseId}/audit/feasibility`),
  listReports: (courseId: string) =>
    request<AnalysisReport[]>(`/courses/${courseId}/analysis-reports`),
  getReport: (courseId: string, reportId: string) =>
    request<AnalysisReport>(
      `/courses/${courseId}/analysis-reports/${reportId}`,
    ),
  // Block 3: Audit-Empfehlung actionable — LLM-Fix-Vorschlag (read-only,
  // das Übernehmen macht das Frontend per PATCH/POST). Kein Credit, 20/h.
  proposeFix: (courseId: string, reportId: string, flawIndex: number) =>
    request<ProposeFixResponse>(
      `/courses/${courseId}/audit/${reportId}/flaws/${flawIndex}/propose-fix`,
      { method: "POST" },
    ),
  exportPdfUrl: (courseId: string, reportId?: string) =>
    `${API_BASE}/courses/${courseId}/export/pdf${
      reportId ? `?report_id=${reportId}` : ""
    }`,
  exportConstructionPdfUrl: (courseId: string) =>
    `${API_BASE}/courses/${courseId}/export/construction.pdf`,
  exportConstructionMdUrl: (courseId: string) =>
    `${API_BASE}/courses/${courseId}/export/construction.md`,

  // Admin
  adminListUsers: (statusFilter?: string) =>
    request<UserMe[]>(
      `/admin/users${statusFilter ? `?status_filter=${statusFilter}` : ""}`,
    ),
  adminApproveUser: (userId: string, audit_balance: number) =>
    request<UserMe>(`/admin/users/${userId}/approve`, {
      method: "POST",
      body: JSON.stringify({ audit_balance }),
    }),
  adminSetBudget: (userId: string, audit_balance: number) =>
    request<UserMe>(`/admin/users/${userId}/budget`, {
      method: "POST",
      body: JSON.stringify({ audit_balance }),
    }),
  adminRejectUser: (userId: string) =>
    request<UserMe>(`/admin/users/${userId}/reject`, { method: "POST" }),

  // Wizard (geführte Neuerstellung)
  suggestRoughIdea: (body: {
    title: string;
    target_group?: string;
    course_format?: string | null;
    duration_hours?: number | null;
    // Bereits im Feld stehender Text (Stichworte/Alt-Beschreibung) —
    // das Modell baut ihn aus bzw. optimiert ihn; leer = Neu-Generierung.
    existing_text?: string;
    strategy?: string;
    language?: ContentLang;
  }) =>
    request<{ rough_idea: string }>("/wizard/suggest-rough-idea", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  suggestILOs: (body: { rough_idea: string; target_group: string; context_kind?: string | null; strategy?: string; language?: ContentLang }) =>
    request<{ ilos: SuggestedILO[] }>("/wizard/suggest-ilos", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  suggestTLAs: (body: { course_meta: WizardCourseMeta; ilos: SuggestedILO[]; strategy?: string; language?: ContentLang }) =>
    request<{ tlas: SuggestedTLA[] }>("/wizard/suggest-tlas", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  suggestATs: (body: {
    course_meta: WizardCourseMeta;
    ilos: SuggestedILO[];
    tlas: SuggestedTLA[];
    strategy?: string;
    language?: ContentLang;
  }) =>
    request<{ ats: SuggestedAT[] }>("/wizard/suggest-ats", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  wizardCatalog: () => request<WizardCatalog>("/wizard/catalog"),

  // Didaktik-Chat („Rede mit der Didaktik-Assistenz", app/routes/chat.py):
  // stateless — History + aktueller Entwurfsstand gehen bei jeder Nachricht mit.
  chatMessage: (body: ChatRequestPayload) =>
    request<ChatResponse>("/chat/message", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // KI-Assistenz (Feld-Umformulierung + Strategie-Batch, app/routes/assist.py)
  strategyPresets: () => request<StrategyPreset[]>("/assist/strategy-presets"),
  // Rewrite geht mit Kurs (course_id) ODER kurs-los (context + language,
  // Wizard-Flow) — beides weglassen liefert 422.
  assistRewrite: (body: {
    course_id?: string;
    field_kind: AssistFieldKind;
    current_text: string;
    instruction: string;
    context?: RewriteContext;
    language?: ContentLang;
  }) =>
    request<RewriteResult>("/assist/rewrite", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  applyStrategy: (body: {
    course_id: string;
    category: AssistCategory;
    instruction?: string;
  }) =>
    request<{ items: ApplyStrategyItem[] }>("/assist/apply-strategy", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Kursinhalte per KI übersetzen (Review-Flow wie applyStrategy):
  // liefert Alt/Neu-Paare, das Übernehmen macht das Frontend per PATCH.
  translateCourse: (courseId: string, target_lang: ContentLang) =>
    request<{ items: TranslateItem[]; target_lang: ContentLang }>(
      "/assist/translate",
      { method: "POST", body: JSON.stringify({ course_id: courseId, target_lang }) },
    ),
};

// --- KI-Assistenz-Types (Single Source: app/routes/assist.py) ---

export type AssistFieldKind =
  | "course_title"
  | "audience"
  | "ilo_text"
  | "ilo_verb"
  | "tla_description"
  | "at_description"
  | "session_title"
  | "custom";

export type AssistCategory = "ilos" | "tlas" | "ats";

export interface StrategyPreset {
  id: string;
  label: string;
  prompt_hint: string;
}

export interface RewriteResult {
  suggestion: string;
  rationale: string;
}

// Kurs-loser Rewrite-Kontext (Wizard-Flow, spiegelt RewriteContext im Backend).
export interface RewriteContext {
  title?: string;
  audience?: string;
  rough_idea?: string;
  strategy?: string;
}

export interface ApplyStrategyItem {
  id: string;
  old_text: string;
  new_text: string;
  rationale: string;
}

// Sprache der Kursinhalte (content_lang) — UI-Sprache ist davon unabhängig.
export type ContentLang = "de" | "en";

// Ein Übersetzungs-Vorschlag aus POST /assist/translate.
// kind: "course" (field: title|audience) | "ilo" (text) | "tla" (description)
// | "at" (description) | "session" (title)
export interface TranslateItem {
  kind: string;
  id: string;
  field: string;
  old_text: string;
  new_text: string;
}

// Lernstrategie pro Kurs (C1): Storage in course.metadata.strategy.
// Beim PATCH immer das komplette Objekt schicken — der flache
// Metadata-Merge im Backend ersetzt den Key "strategy" ganz.
export interface StrategyValue {
  global_text: string;
  presets: string[];
  ilo_text: string;
  tla_text: string;
  at_text: string;
}

export const EMPTY_STRATEGY: StrategyValue = {
  global_text: "",
  presets: [],
  ilo_text: "",
  tla_text: "",
  at_text: "",
};

export function strategyFromMetadata(
  metadata?: Record<string, unknown>,
): StrategyValue {
  const raw = (metadata?.strategy ?? {}) as Record<string, unknown>;
  return {
    global_text: typeof raw.global_text === "string" ? raw.global_text : "",
    presets: Array.isArray(raw.presets)
      ? raw.presets.filter((p): p is string => typeof p === "string")
      : [],
    ilo_text: typeof raw.ilo_text === "string" ? raw.ilo_text : "",
    tla_text: typeof raw.tla_text === "string" ? raw.tla_text : "",
    at_text: typeof raw.at_text === "string" ? raw.at_text : "",
  };
}

// --- Wizard-Types (Single Source: app/audit/suggestion.py) ---

export type WizardContextKind = "hochschule" | "ausbildung" | "corporate_ld";

export interface WizardCourseMeta {
  title: string;
  target_group: string;
  course_format?: string | null;
  duration_hours?: number | null;
  context_kind?: WizardContextKind | null;
}

export interface SuggestedILO {
  text: string;
  action_verb: string;
  bender_depth: DepthLabel;
  rationale: string;
  // Kurs-Kontext (Block 3): UUID des bestehenden Eintrags, vom Server
  // angereichert (Position in der Liste = Nummer im Kurs). Fehlt = Neuanlage.
  id?: string;
}

export interface SuggestedTLA {
  type: string;
  description: string;
  duration_minutes?: number | null;
  modality?: string | null;
  // Zeitliche Phase (Multi-Session-Design), optional vom LLM mitgeliefert
  phase?: string;
  linked_ilo_indices: number[];
  rationale: string;
  id?: string; // Kurs-Kontext (Block 3), siehe SuggestedILO
  linked_ilo_ids?: string[]; // Kurs-Kontext: aufgelöste UUIDs der Indices
}

export interface SuggestedAT {
  type: string;
  description: string;
  format?: string | null;
  duration_minutes?: number | null;
  linked_ilo_indices: number[];
  rationale: string;
  id?: string; // Kurs-Kontext (Block 3), siehe SuggestedILO
  linked_ilo_ids?: string[]; // Kurs-Kontext: aufgelöste UUIDs der Indices
}

// --- Didaktik-Chat-Types (Single Source: app/routes/chat.py) ---

export interface ChatSource {
  title: string;
  url: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  // Client-seitige Anreicherung für die Anzeige (das Backend ignoriert die
  // Extra-Felder in der mitgeschickten History): Quellen der Recherche und
  // ob die Nachricht mit aktiver Recherche angefragt wurde (Fallback-Zeile).
  sources?: ChatSource[];
  research?: boolean;
}

export type ChatProposalTarget = "ilos" | "tlas" | "ats";

// Vorschlag der Assistenz: komplette neue Liste für eine Kategorie
// (Items im Suggested-Shape der Ziel-Kategorie, inkl. linked_ilo_indices).
export interface ChatProposal {
  target: ChatProposalTarget;
  items: Array<SuggestedILO | SuggestedTLA | SuggestedAT>;
  rationale: string;
}

export interface ChatRequestPayload {
  messages: ChatMessage[];
  // "wizard" (Default, Entwurfsstand kommt mit) oder "course" (Block 3:
  // der Server lädt den persistierten Kurs über course_id selbst).
  context?: "wizard" | "course";
  course_id?: string;
  // Pflicht nur im Wizard-Kontext:
  step?: 2 | 3 | 4;
  course_meta?: WizardCourseMeta;
  ilos?: SuggestedILO[];
  tlas?: SuggestedTLA[];
  ats?: SuggestedAT[];
  strategy?: string;
  language?: ContentLang;
  // Echte Web-Recherche für diese Nachricht (metered — Opt-in, Default aus)
  enable_research?: boolean;
}

export interface ChatResponse {
  reply: string;
  proposal: ChatProposal | null;
  // Quellen der Recherche (leer ohne enable_research oder ohne Treffer)
  sources?: ChatSource[];
}

// --- propose-fix-Types (Block 3, Single Source: app/routes/audit.py) ---

export type FixActionKind =
  | "update_ilo"
  | "update_tla"
  | "update_at"
  | "link_tla"
  | "link_at"
  | "create_tla"
  | "create_at";

// Eine validierte, auf UUIDs gemappte Fix-Aktion. update_*/link_* tragen die
// UUID + Nummer des Bestands-Eintrags; create_* haben noch keine UUID.
export interface FixAction {
  action: FixActionKind;
  ilo_id?: string | null;
  tla_id?: string | null;
  at_id?: string | null;
  ilo_number?: number | null;
  tla_number?: number | null;
  at_number?: number | null;
  linked_ilo_ids?: string[] | null;
  linked_ilo_numbers?: number[] | null;
  text?: string | null;
  action_verb?: string | null;
  bender_depth?: BenderDepth | null;
  type?: string | null;
  description?: string | null;
  format?: string | null;
  modality?: string | null;
  duration_minutes?: number | null;
}

// Verworfene LLM-Aktion mit Grund (Geister-Regel: nie still verwerfen).
export interface DroppedAction {
  action: string;
  reason: string;
}

export interface ProposeFixResponse {
  flaw_index: number;
  rationale: string;
  actions: FixAction[];
  dropped_actions: DroppedAction[];
  // Staleness-Guard: der Kurs wurde nach diesem Audit geändert.
  course_newer_than_report: boolean;
}

// Catalog (Klartext-Labels für Wizard-Dropdowns)
export interface CatalogEntry {
  id: string;
  name: string;
  description?: string | null;
}

export interface FormatEntry {
  id: string;
  name: string;
  description: string;
}

export interface CourseFormatEntry {
  id: string;
  name: string;
  category: string;
  category_label: string;
  description: string;
  activation?: string | null;
  differentiation?: string | null;
}

export interface WizardCatalog {
  tla_types: CatalogEntry[];
  at_types: CatalogEntry[];
  at_formats: FormatEntry[];
  course_formats: CourseFormatEntry[];
  context_formats: Record<string, FormatEntry[]>;
}
